// DocumentSummary.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatFileSize } from 'utilities/Files';
import { isPdfMimeType } from 'utilities/fileMime';

// CSS
import './DocumentSummary.css';

// Icons
import DocumentIcon from 'svg/DocumentIcon';
import Icon from 'svg/Icon';
import PdfIcon from 'svg/PdfIcon';
import PresentationIcon from 'svg/PresentationIcon';
import SpreadsheetIcon from 'svg/SpreadsheetIcon';

/**
 * DocumentSummary
 *
 * Displays a summary of a document node, including author, title, file info, and date.
 *
 * @param {object} node - The document node object.
 * @param {Function} onClick - Click handler for the summary.
 * @param {Boolean} focused - Whether the summary is focused.
 *
 * @returns {ReactNode} DocumentSummary component.
 */
const DocumentSummary = ({ node, onClick, focused }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [hovered, setHovered] = useState(false);

  const userName = node?.user?.name || 'Anonymous';

  // Get document title from node title or filename
  const documentTitle = node?.title || node?.data_document?.filename || 'Untitled Document';

  // Get document metadata
  const fileSize = node?.data_document?.size;
  const rawExtension = node?.data_document?.extension || node?.fileName?.split('.')?.pop();
  const fileExtension = rawExtension ? rawExtension.toUpperCase() : undefined;
  const mimeType = (node?.data_document?.mimeType || '').toLowerCase();

  // ----- MEMOS ----- //

  /**
   * Formatted Size
   * 
   * Formats the file size into a human-readable string.
   * E.g., "1.2 MB"
   * 
   * @returns {String} Formatted file size.
   */

  const formattedSize = useMemo(() => formatFileSize(fileSize), [fileSize]);

  /**
   * File Info
   * 
   * Combines file extension and formatted size into a single string.
   * E.g., "PDF • 1.2 MB"
   * 
   * @returns {String} File info string.
   */
  const fileInfo = useMemo(() => {
    const parts = [];
    if (fileExtension) parts.push(fileExtension);
    if (formattedSize) parts.push(formattedSize);
    return parts.join(' • ');
  }, [fileExtension, formattedSize]);

  const IconComponent = useMemo(() => {
    const ext = (rawExtension || '').toLowerCase();

    if (ext === 'pdf' || isPdfMimeType(mimeType)) {
      return PdfIcon;
    }

    if (['ppt', 'pptx', 'pps', 'ppsx', 'odp', 'key'].includes(ext) || mimeType.startsWith('application/vnd.ms-powerpoint') || mimeType.includes('presentation')) {
      return PresentationIcon;
    }

    if (['xls', 'xlsx', 'csv', 'tsv', 'ods', 'xlsm', 'numbers'].includes(ext) || mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return SpreadsheetIcon;
    }

    return DocumentIcon;
  }, [rawExtension, mimeType]);

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div
      className={`document-summary-container${focused ? ' focused' : ''}`}
      style={{
        backgroundColor: hovered ? theme.backgroundHover : undefined,
      }}
      role="button"
      tabIndex={0}
      aria-label={`${userName} uploaded ${documentTitle}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >

      {/* LEFT SECTION */}
      <div className="document-summary-left">

        {/* FILE ICON */}
        <span className="document-summary-file-icon" aria-hidden="true">
          <IconComponent width={40} height={40} />
        </span>

      </div>

      {/* CENTER */}
      <div className="document-summary-center">

        {/* CENTER TOP */}
        <div className="document-summary-center-top">

          {/* TITLE */}
          <div
            className="document-summary-title"
          >{documentTitle}</div>

        </div>

        {/* CENTER BOTTOM */}
        <div className="document-summary-center-bottom">

          {/* AUTHOR */}
          <span
            className="document-summary-author"
          >{userName}</span>

          {/* FILE INFO */}
          {fileInfo && (
            <span
              className="document-summary-file-info"
            > • {fileInfo}</span>
          )}

        </div>
      </div>

      {/* OPEN */}
      <div
        className="document-summary-open"
        title="Open"
      >
        <Icon
          iconKey="chevron_right"
          color={theme.foreground}
          width={16}
          height={16}
        />
      </div>

    </div>
  );
};

// Dev Tools
DocumentSummary.displayName = 'DocumentSummary';

export default DocumentSummary;
